import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import StudentLayout from '../../components/layout/StudentLayout';
import {
  Box,
  Checkbox,
  Button,
  Snackbar,
  Alert,
  Grid,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/FileDownload';
import CommentIcon from '@mui/icons-material/Comment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import revisionService from '../../services/RevisionService';
import api from '../../services/api';

// NOTE_OPTIONS duplicated locally to keep file standalone
const NOTE_OPTIONS = ['Silly mistake', 'New topic for me', 'Clarify concepts'];

export default function RevisionNotesPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  /* ─────────── State ─────────── */
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState([]);
  const [filters, setFilters] = useState({ category: '', topic: '', rechecked: '' });

  /* dialogs */
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
    const [snack, setSnack] = useState({ open: false, msg: '', severity: 'error' });
  const [alert, setAlert] = useState({ open: false, msg: '' });
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [dialogContent, setDialogContent] = useState(null); // {type:'question'|'comment', data:…}

  /* ─────────── Helpers ─────────── */
    // Build a proxied URL so the browser can access private object-storage images
    const proxyImgUrl = (url) => {
    try {
      const u = new URL(url, window.location.origin);
      // If the URL points to same backend host (e.g. localhost:8000) just use its pathname so dev-server proxy kicks in
      if (u.hostname === 'localhost' || u.hostname === window.location.hostname) {
        return u.pathname + u.search;
      }
      // Otherwise hit generic proxy endpoint (ensure backend has one in prod)
      return `/api/proxy/?url=${encodeURIComponent(url)}`;
    } catch (e) {
      return url; // best-effort fallback
    }
  };



  const fetchNotes = async () => {
    setLoading(true);
    try {
      const { data } = await revisionService.list({}); // student filter happens server-side
      setNotes(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      console.error('Failed to load revision notes', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchNotes(); }, []);

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredNotes.map((n) => n.id));
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

    const generatePdf = async () => {
      console.log('Download clicked');
    if (pdfGenerating) return;
        if (selectedIds.length === 0) {
      setAlert({ open: true, msg: 'Please select at least one question before downloading.' });
      return;
    }
    if (selectedIds.length > 20) {
      setSnack({ open: true, msg: 'Max 20 questions can be selected for PDF generation', severity: 'error' });
      return;
    }

    setPdfGenerating(true);
    try {
    const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
    if (!pdf) {
      console.error('Failed to load jsPDF');
      setSnack({ open: true, msg: 'PDF library failed to load', severity: 'error' });
      return;
    }
    const selectedNotes = filteredNotes.filter((n) => selectedIds.includes(n.id));
    const margin = 40;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight() - margin;
    const headerText = 'Reference notes for clarifications';
    pdf.setFontSize(16);
    pdf.text(headerText, pageWidth / 2, margin, { align: 'center' });
        pdf.setDrawColor(0);
        pdf.setLineWidth(1);
        pdf.rect(margin / 2, margin / 2, pageWidth - margin, pdf.internal.pageSize.getHeight() - margin, 'S');
    const lineHeight = 14;
    let y = margin + 20;
    for (let idx = 0; idx < selectedNotes.length; idx++) {
     
      const note = selectedNotes[idx];
      pdf.setFontSize(12);
      pdf.text(`${idx + 1}. ${note.question_text}`, 40, y);
      y += lineHeight;
      if (note.image_proxy_url) {
        try {
                    const imgResp = await fetch(proxyImgUrl(note.image_proxy_url), { credentials: 'include', cache: 'no-store' });
          const blob = await imgResp.blob();
          const dataUrl = await new Promise((res) => {
            const reader = new FileReader();
            reader.onload = () => res(reader.result);
            reader.readAsDataURL(blob);
          });
                    const format = blob.type === 'image/jpeg' ? 'JPEG' : 'PNG';
          const imgProps = pdf.getImageProperties(dataUrl);
          const imgWidth = Math.min(400, imgProps.width);
          const imgHeight = (imgProps.height * imgWidth) / imgProps.width;
          if (y + imgHeight > pageHeight) {
            pdf.addPage();
            pdf.setFontSize(16);
            pdf.text(headerText, pageWidth / 2, margin, { align: 'center' });
        pdf.setDrawColor(0);
        pdf.setLineWidth(1);
        pdf.rect(margin / 2, margin / 2, pageWidth - margin, pdf.internal.pageSize.getHeight() - margin, 'S');
            y = margin + 20;
          }
                    pdf.addImage(dataUrl, format, 40, y, imgWidth, imgHeight);
          y += imgHeight + lineHeight;
        } catch (e) {}
      }
      if (note.question_options?.length) {
        note.question_options.forEach((opt, optIdx) => {
          pdf.text(`${String.fromCharCode(65 + optIdx)}. ${opt}`, 60, y);
          y += lineHeight;
        });
      }
      pdf.text(`Correct answer: ${note.correct_answer_display || '—'}`, 60, y);
      y += lineHeight;
      pdf.text(`Your answer: ${note.student_answer_display || '—'}`, 60, y);
      y += lineHeight;
      pdf.text(`Note category: ${note.student_note_category || '—'}`, 60, y);
      y += lineHeight * 2;
      if (y > pageHeight - 100 && idx < selectedNotes.length - 1) {
        pdf.addPage();
        pdf.setFontSize(16);
        pdf.text(headerText, pageWidth / 2, margin, { align: 'center' });
        pdf.setDrawColor(0);
        pdf.setLineWidth(1);
        pdf.rect(margin / 2, margin / 2, pageWidth - margin, pdf.internal.pageSize.getHeight() - margin, 'S');
        y = margin + 20;
      }
    }
    console.log('calling pdf.save, pages=', pdf.getNumberOfPages());
    pdf.save('revision-notes.pdf');
    } catch (err) {
      console.error('PDF generation failed', err);
      setSnack({ open: true, msg: 'PDF generation failed', severity: 'error' });
    } finally {
      setPdfGenerating(false);
    }
  };

  const handleViewQuestion = (note) => {
    const q = {
      question_text: note.question_text || '',
      options: note.question_options || [],
      student_answer: note.student_answer_display,
      correct_answer: note.correct_answer_display,
      image_url: note.image_proxy_url || null,
    };
    setDialogContent({ type: 'question', data: q, note });
    setDialogOpen(true);
  };

  const handleViewComment = (note) => {
    setDialogContent({ type: 'comment', data: note.teacher_comment });
    setDialogOpen(true);
  };

  const toggleRechecked = async (note) => {
    const updated = { rechecked: !note.rechecked };
    try {
      await revisionService.update(note.id, updated);
      setNotes((arr) =>
        arr.map((x) => (x.id === note.id ? { ...x, ...updated } : x))
      );
    } catch (e) {
      console.error('Failed to update rechecked', e);
    }
  };

  /* ─────────── Filtering ─────────── */
  const filteredNotes = notes.filter((n) => {
    const matchCat = filters.category ? n.student_note_category === filters.category : true;
    const matchTopic = filters.topic
      ? (n.topic_name || '').toLowerCase().includes(filters.topic.toLowerCase())
      : true;
    const matchRe = filters.rechecked === ''
      ? true
      : filters.rechecked === 'yes'
        ? n.rechecked
        : !n.rechecked;
    return matchCat && matchTopic && matchRe;
  });

  const allSelected = filteredNotes.length > 0 && selectedIds.length === filteredNotes.length;
  const indeterminate = selectedIds.length > 0 && !allSelected;

  /* ─────────── UI ─────────── */
  return (
    <StudentLayout>
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 4 }, overflowX: 'hidden' }}>
        <IconButton onClick={() => navigate(-1)} size="medium" sx={{ mb: 1 }} aria-label="Go back">
          <ArrowBackIcon />
        </IconButton>

        <Grid container spacing={2}>
          {/* ───── Filters ───── */}
          <Grid item xs={12} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: { xs: 2, sm: 2 } }}>
                <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  Filters
                </Typography>
                <Stack spacing={2}>
                  {/* Note Category */}
                  <FormControl fullWidth size="small">
                    <InputLabel>Note Category</InputLabel>
                    <Select
                      label="Note Category"
                      value={filters.category}
                      onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}
                    >
                      <MenuItem value="">All</MenuItem>
                      {NOTE_OPTIONS.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Topic */}
                  <TextField
                    size="small"
                    label="Topic"
                    value={filters.topic}
                    onChange={(e) => setFilters((f) => ({ ...f, topic: e.target.value }))}
                  />

                  {/* Revisited & clear */}
                  <FormControl fullWidth size="small">
                    <InputLabel>Revisited & Clear</InputLabel>
                    <Select
                      label="Revisited & Clear"
                      value={filters.rechecked}
                      onChange={(e) => setFilters((f) => ({ ...f, rechecked: e.target.value }))}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="yes">Yes</MenuItem>
                      <MenuItem value="no">No</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* ───── Notes list (cards on mobile, table on desktop) ───── */}
          <Grid item xs={12} md={9}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Card sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                    <Typography variant="h6" sx={{ m: 0, fontSize: { xs: '1.1rem', sm: '1.25rem' }, fontWeight: 600 }}>
                      My Revision Notes
                    </Typography>
                    <Button
                      variant="contained"
                      size="medium"
                      startIcon={<DownloadIcon />}
                      onClick={generatePdf}
                      disabled={pdfGenerating}
                      fullWidth={isMobile}
                      sx={{ minHeight: 44, alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                    >
                      Download PDF
                    </Button>
                  </Box>

                  {isMobile ? (
                    /* Mobile: card list */
                    <>
                      {filteredNotes.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                          No records found.
                        </Typography>
                      ) : (
                        <Stack spacing={1.5}>
                          <Box sx={{ display: 'flex', alignItems: 'center', pb: 0.5 }}>
                            <Checkbox
                              indeterminate={indeterminate}
                              checked={allSelected}
                              onChange={toggleSelectAll}
                              size="small"
                            />
                            <Typography variant="body2" color="text.secondary" onClick={toggleSelectAll} sx={{ cursor: 'pointer' }}>
                              Select all
                            </Typography>
                          </Box>
                          {filteredNotes.map((n) => (
                            <Card key={n.id} variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
                              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                                  <Checkbox
                                    checked={selectedIds.includes(n.id)}
                                    onChange={() => toggleSelectOne(n.id)}
                                    size="small"
                                    sx={{ p: 0.5 }}
                                  />
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {n.student_note_category || '—'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                      <IconButton size="small" onClick={() => handleViewQuestion(n)} aria-label="View question" sx={{ p: 0.5 }}>
                                        <VisibilityIcon fontSize="small" />
                                      </IconButton>
                                      {n.teacher_comment ? (
                                        <IconButton size="small" onClick={() => handleViewComment(n)} aria-label="View teacher comment" sx={{ p: 0.5 }}>
                                          <CommentIcon fontSize="small" />
                                        </IconButton>
                                      ) : null}
                                      <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Revisited</Typography>
                                        <Switch
                                          checked={Boolean(n.rechecked)}
                                          onChange={() => toggleRechecked(n)}
                                          size="small"
                                          color="primary"
                                        />
                                      </Box>
                                    </Box>
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          ))}
                        </Stack>
                      )}
                    </>
                  ) : (
                    /* Desktop: table */
                    <TableContainer sx={{ overflowX: 'auto' }}>
                      <Table size="small" sx={{ minWidth: 400 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox">
                              <Checkbox
                                indeterminate={indeterminate}
                                checked={allSelected}
                                onChange={toggleSelectAll}
                              />
                            </TableCell>
                            <TableCell>View</TableCell>
                            <TableCell>Note Category</TableCell>
                            <TableCell>Teacher Comment</TableCell>
                            <TableCell>Revisited & clear?</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredNotes.map((n) => (
                            <TableRow key={n.id} hover selected={selectedIds.includes(n.id)}>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={selectedIds.includes(n.id)}
                                  onChange={() => toggleSelectOne(n.id)}
                                />
                              </TableCell>
                              <TableCell>
                                <IconButton size="small" onClick={() => handleViewQuestion(n)}>
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                              <TableCell>{n.student_note_category}</TableCell>
                              <TableCell>
                                {n.teacher_comment ? (
                                  <IconButton size="small" onClick={() => handleViewComment(n)}>
                                    <CommentIcon fontSize="small" />
                                  </IconButton>
                                ) : (
                                  '—'
                                )}
                              </TableCell>
                              <TableCell>
                                <Switch
                                  checked={Boolean(n.rechecked)}
                                  onChange={() => toggleRechecked(n)}
                                  size="small"
                                  color="primary"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                          {!filteredNotes.length && (
                            <TableRow>
                              <TableCell colSpan={5} align="center">
                                No records found.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>

        {/* ───── Dialogs ───── */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { m: { xs: 2 }, maxHeight: 'calc(100vh - 32px)' } }}
        >
          {dialogContent?.type === 'question' && (
            <>
              <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, py: { xs: 1.5, sm: 2 } }}>
                Question Preview
              </DialogTitle>
              <DialogContent dividers sx={{ px: { xs: 2, sm: 3 } }}>
                <Typography sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>{dialogContent.data.question_text}</Typography>

                {dialogContent.data.image_url && (
                  <Box sx={{ mb: 2 }}>
                    <img
                      src={proxyImgUrl(dialogContent.data.image_url)}
                      alt="question"
                      style={{ maxWidth: '100%' }}
                    />
                  </Box>
                )}

                {dialogContent.data.options?.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    {dialogContent.data.options.map((opt, idx) => (
                      <Typography key={idx}>
                        {String.fromCharCode(65 + idx)}. {opt}
                      </Typography>
                    ))}
                  </Box>
                )}

                <Typography variant="subtitle2">
                  Your answer:{' '}
                  {dialogContent.data.student_answer || '—'}
                </Typography>
                <Typography variant="subtitle2">
                  Correct answer:{' '}
                  {dialogContent.data.correct_answer || '—'}
                </Typography>
              </DialogContent>
            </>
          )}

          {dialogContent?.type === 'comment' && (
            <>
              <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, py: { xs: 1.5, sm: 2 } }}>
                Teacher Comment
              </DialogTitle>
              <DialogContent dividers sx={{ px: { xs: 2, sm: 3 } }}>
                <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>{dialogContent.data}</Typography>
              </DialogContent>
            </>
          )}
        </Dialog>

        {/* Alert dialog */}
        <Dialog open={alert.open} onClose={() => setAlert({ ...alert, open: false })} PaperProps={{ sx: { m: 2 } }}>
          <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, py: { xs: 1.5, sm: 2 } }}>
            Download PDF
          </DialogTitle>
          <DialogContent dividers sx={{ px: { xs: 2, sm: 3 } }}>
            <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>{alert.msg}</Typography>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" onClick={() => setAlert({ ...alert, open: false })} sx={{ minHeight: 44 }}>
                OK
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      </Box>
    </StudentLayout>
  );
}